import { LastCollectionStoreModel } from 'app/store/last-collection-store'
import { UserStoreModel } from 'app/store/user-store'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { QuizStoreModel } from '../quiz-store'
import { OnboardingStoreModel } from '../onboarding-store'

/**
 * Modal state for upgrade flow modals.
 * - showUpsellModal/showCelebrationModal: volatile (transient UI state)
 * - lastCelebratedUpgradeKey: persisted dedupe key so the same event is not shown repeatedly
 * - upgradeReturnPath: persisted (tracks where to navigate after upgrade)
 */
const UpgradeModalStateModel = types
  .model('UpgradeModalState', {
    upgradeReturnPath: types.maybe(types.string),
    lastCelebratedUpgradeKey: types.optional(types.string, ''),
  })
  .volatile(() => ({
    showUpsellModal: false,
    showCelebrationModal: false,
    pendingCelebrationKey: undefined as string | undefined,
    pendingCelebrationNeedsAck: false,
  }))
  .actions((self) => ({
    openUpsellModal() {
      self.showUpsellModal = true
    },
    closeUpsellModal() {
      self.showUpsellModal = false
    },
    queueCelebrationModal(event: { key: string; needsAck?: boolean }) {
      if (!event.key) return
      if (self.lastCelebratedUpgradeKey === event.key) return
      self.pendingCelebrationKey = event.key
      self.pendingCelebrationNeedsAck = !!event.needsAck
      self.showCelebrationModal = true
    },
    openCelebrationModal() {
      self.pendingCelebrationKey = `manual:${Date.now()}`
      self.pendingCelebrationNeedsAck = false
      self.showCelebrationModal = true
    },
    closeCelebrationModal() {
      self.showCelebrationModal = false
    },
    finalizeCelebrationModal() {
      const needsAck = self.pendingCelebrationNeedsAck

      if (self.pendingCelebrationKey) {
        self.lastCelebratedUpgradeKey = self.pendingCelebrationKey
      }

      self.pendingCelebrationKey = undefined
      self.pendingCelebrationNeedsAck = false
      self.showCelebrationModal = false

      return needsAck
    },
    setUpgradeReturnPath(path: string) {
      self.upgradeReturnPath = path
    },
    clearUpgradeReturnPath() {
      self.upgradeReturnPath = undefined
    },
  }))

/**
 * Modal state for streak celebration popup.
 * - showStreakCelebrationModal: volatile (transient UI state)
 * - lastStreakCelebrationShownAt: persisted (tracks last date shown)
 */
const StreakCelebrationStateModel = types
  .model('StreakCelebrationState', {
    lastStreakCelebrationShownAt: types.optional(types.string, ''),
  })
  .volatile(() => ({
    showStreakCelebrationModal: false,
  }))
  .views((self) => ({
    hasShownStreakCelebrationToday(): boolean {
      if (!self.lastStreakCelebrationShownAt) return false
      const today = new Date().toISOString().split('T')[0]
      return self.lastStreakCelebrationShownAt === today
    },
  }))
  .actions((self) => ({
    openStreakCelebrationModal() {
      self.showStreakCelebrationModal = true
    },
    closeStreakCelebrationModal() {
      self.showStreakCelebrationModal = false
    },
    markStreakCelebrationShown(date: string) {
      self.lastStreakCelebrationShownAt = date
    },
  }))

/**
 * A RootStore model.
 */
// prettier-ignore
export const RootStoreModel = types.model("RootStore").props({
  userStore: types.optional(UserStoreModel, {} as any),
  lastCollectionStore: types.optional(LastCollectionStoreModel, {} as any),
  quizStore: types.optional(QuizStoreModel, {} as any),
  onboardingStore: types.optional(OnboardingStoreModel, {} as any),
  openedUrl: types.optional(types.string, ''),
  upgradeModalState: types.optional(UpgradeModalStateModel, {}),
  streakCelebrationState: types.optional(StreakCelebrationStateModel, {}),
}).volatile(() => ({
  isHydrated: false,
})).actions(self => ({
  setOpenedUrl(url: string) {
    self.openedUrl = url
  },
  setHydrated() {
    self.isHydrated = true
  },
}))

/**
 * The RootStore instance.
 */
export interface RootStore extends Instance<typeof RootStoreModel> {}

/**
 * The data of a RootStore.
 */
export interface RootStoreSnapshot extends SnapshotOut<typeof RootStoreModel> {}
